










































































import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Route, RawLocation } from 'vue-router';

import { KCardHeader, KCardHeaderBtn, KCardFooter, KCardFooterBtn, KSpinner } from '@kasasa/fbase-components';
import { AuthGroupManager, ExpandableFilter, Alert, NoticeClass, Dialog, NoticeResponse } from '@kasasa/fbase-components/lib';

import NavigationHeader from '@/components/NavigationHeader.vue';
import NodePanel from "@/components/NodePanel.vue";
import HIRT from "@/components/hirt";

import uid from "@/utils/uid";
import { generateLibrary } from "@/utils/genLibrary";

import Nav, {navFactory} from '@/services/Navigation';
import NavNode from "@/services/NavigationNode";
import NavService from "@/services/NavigationService";
import SMService from "@/services/SiteMapService";

const auth = namespace('auth');

@Component({
	components: {
		NavigationHeader,
		NodePanel,
		KCardFooter,
		KCardFooterBtn,
		KCardHeader,
		KCardHeaderBtn,
		KSpinner
	}
})
export default class NavigationFormPage extends Vue {
	@auth.State('authManager') authManager!: AuthGroupManager;

	nav: Nav = {} as Nav;
	nodeTree: NavNode[] = [];
	siteMap: NavNode[] = [];

	isLoaded = false;

	loadedData = '';

	navSvc = new NavService(this.$store);
	smSvc = new SMService(this.$store);

	get isDirty() {
		return this.loadedData !== JSON.stringify(this.nav.nodes);
	}

	get ro() {
		return !this.authManager.canWrite('navs');
	}

	get showDelete() {
		return this.$route.meta.mode === 'edit';
	}

	loadSitemap() {
		this.siteMap = [];
		this.smSvc.find(this.$route.params.clientId, this.$route.params.siteId)
			.then(resp => {
				const sm = generateLibrary(resp.data.data.live);
				uid(sm);
				this.siteMap = sm;
			});
	}

	@Watch('$route', {immediate: true, deep: true})
	loadNavs() {
		// load sitemap for Library component to use
		this.loadSitemap();

		// load the Navigation object
		if (this.$route.meta.mode === 'edit') {
			this.navSvc.find(this.$route.params.clientId, this.$route.params.siteId, this.$route.params.id, (new ExpandableFilter()).expand('nodes'))
				.then(resp => {
					this.nav = resp.data.data;

					//  Apply a uuid to each node recursively
					uid(this.nav.nodes);

					// store initial state - must be inside promise
					this.setLoadedData(this.nav.nodes);

					this.isLoaded = true;
				})
				.catch(() => {
					// @todo: handle 404
					// no errors besides 401, 403 and 500 to listen for.
				});
		} else {
			this.nav = navFactory();
			this.isLoaded = true;

			// store initial state
			this.setLoadedData(this.nav.nodes);
		}

	}

	setLoadedData(nodes: NavNode[]) {
		this.loadedData = JSON.stringify(nodes);
		this.$store.dispatch('setExpanded', true);
	}

	get title() {
		return this.$route.meta.mode == 'add' ? 'Add New Navigation' : 'Edit';
	}

	get crumbs() {
		const crumbs = [];
		crumbs.push(
			{
				key: '0',
				text: 'Navigations',
				disabled: false,
				link: true,
				exact: true,
				to: { name: 'NavList', params: { clientId: this.$route.params.clientId, siteId: this.$route.params.siteId } }
			}
		);

		if (this.$route.meta.mode == 'add') {
			crumbs.push({
				key:'1',
				text: 'Add New Navigation',
				disabled: true
			});
		} else {
			crumbs.push({
				key: '2',
				text: this.title,
				disabled: true
			});
		}
		return crumbs;
	}

	async deleteNav() {
		const dialog = new Dialog(
			'Delete this Navigation?',
			`Are you sure you want to delete "${this.nav.name}"? Caution: this can't be undone.`,
			'DELETE'
		);

		dialog.setDeclineLabel('CANCEL')
			.setDismissable(false);
		const success = new Alert(`${this.nav.name} has been Deleted.`, NoticeClass.SUCCESS);

		success.setTimeout(6000);

		const res = await this.$store.dispatch('notices/add', dialog);
		switch (res) {
			case NoticeResponse.ACCEPT:
				this.navSvc.delete(this.$route.params.clientId, this.$route.params.siteId, this.$route.params.id)
					.then(() => {
						this.$store.dispatch('notices/add', success);
						this.goToNavList();
					})
					.catch((err) => {
						// might be 409, in which case we have a prepared snackbar
						switch (err.response.status) {
							case 409:
							default:
								this.$store.dispatch('notices/add', new Alert(
									`This ${this.nav.name} is in use by a Block.`,
									NoticeClass.ERROR
								));
								break;
						}
					});
				break;
			case NoticeResponse.DECLINE:
			default:
				// do nothing
				// the modal closes
				break;
		}
	}

	cancelAction() {
		// beforeRouteLeave() will detect unsaved changes
		this.goToNavList();
	}

	generateSaveActionPromise(): Promise<void> {
		const saved = new Alert(`${this.nav.name} is successfully saved.`, NoticeClass.SUCCESS);
		saved.setTimeout(6000);

		return new Promise((resolve, reject) => {
			if (this.$route.meta.mode === 'add') {
				this.navSvc.create(this.$route.params.clientId, this.$route.params.siteId, this.nav)
					.then((resp) => {
						this.$store.dispatch('notices/add', saved);

						// just in case
						this.nav = resp.data.data;
						uid(this.nav.nodes);

						// reset all the vuelidates and loadedData;
						this.reset();

						// will send to new FormPage, however saveAndClose will override and send to navlist
						const params = {...this.$route.params, id: resp.data.data.id || ''};
						const route = {params: params, name: 'NavForm'} as RawLocation;
						this.$router.push(route);

						resolve();
					})
					.catch(() => {
						const error = new Alert(`${this.nav.name} failed to save.`, NoticeClass.ERROR);
						error.setTimeout(6000);
						this.$store.dispatch('notices/add', error);
						reject();
					});
			} else {
				this.navSvc.update(this.$route.params.clientId, this.$route.params.siteId, this.$route.params.id, this.nav)
					.then((resp) => {
						this.nav = resp.data.data;
						uid(this.nav.nodes);
						this.$store.dispatch('notices/add', saved);

						// reset all the vuelidates;
						this.reset();

						// refresh sitemap
						this.loadSitemap();

						resolve();
					})
					.catch(() => {
						const saved = new Alert(`${this.nav.name} failed to save.`, NoticeClass.ERROR);
						saved.setTimeout(6000);
						this.$store.dispatch('notices/add', saved);
						reject();
					});

			}
		});
	}

	saveAction(): Promise<void> | void {
		if (this.checkForErrors()) {
			return;
		}

		return this.generateSaveActionPromise();
	}

	saveCloseAction(): Promise<void> | void {
		if (this.checkForErrors()) {
			return;
		}

		return this.generateSaveActionPromise().then(() => {
			this.goToNavList();
		});
	}

	goToNavList() {
		const route = {params: this.$route.params, name: 'NavList'} as RawLocation;
		this.$router.push(route);
	}

	checkForErrors() {
		let errors = false;

		const header = this.$refs.navHeader as NavigationHeader & HIRT;

		header.touch();

		errors = header.hasErrors();

		if (errors) {
			const broken = new Alert('Unable to save. Please make sure all required fields are completed without errors.', NoticeClass.ERROR);
			this.$store.dispatch('notices/add', broken);
		}

		return errors;
	}

	checkForDirty() {
		return (this.$refs.navHeader as NavigationHeader & HIRT).isDirty() || this.isDirty;
	}

	hasErrors() {
		return (this.$refs.navHeader as NavigationHeader & HIRT).hasErrors();
	}

	reset() {
		this.setLoadedData(this.nav.nodes);
		(this.$refs.navHeader as NavigationHeader & HIRT).reset();
	}

	touch() {
		(this.$refs.navHeader as NavigationHeader & HIRT).touch();
	}

	async beforeRouteLeave(to: Route, from: Route, next: Function) {
		if (this.checkForDirty()) {
			const dialog = new Dialog('Unsaved Changes', 'You have unsaved changes on this page that will be lost if you leave now. Are you sure?', 'LEAVE WITHOUT SAVING');
			dialog.setDeclineLabel('STAY ON THIS PAGE')
				.setDismissable(false);

			const res = await this.$store.dispatch('notices/add', dialog);
			switch (res) {
				case NoticeResponse.ACCEPT:
					this.reset();
					next();
					break;
				case NoticeResponse.DECLINE:
				default:
					// staying on the page
					break;
			}
		} else {
			this.reset();
			next();
		}
	}
}
